import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createProject } from '@/api/v4/projects.api';
import { QueryKey } from '@/config/query-client';

import { useProjects } from './projects-list/use-projects.query';
import {
  BaseExceptions,
  getMessageFromErrorResponse,
  isBaseError,
} from '@/api/v4/base-fetch';
import { notify } from '@/ui/snackbar/notify';
import { useTranslation } from 'react-i18next';

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  const { refetch: refreshProjects } = useProjects();
  const { t } = useTranslation();

  const createProjectMutation = useMutation({
    mutationFn: createProject,
    onSuccess: async () => {
      await refreshProjects();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKey.ProjectsKpi] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.ProjectOptions] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.CompaniesStatistics],
        }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.ActiveProjects] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.LastActivities] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.LastActivitiesCount],
        }),
      ]);
    },
    onError(error) {
      let message = t`default:unknownError`;

      if (
        isBaseError(error) &&
        error.response.status === BaseExceptions.UNPROCESSABLE_ENTITY
      ) {
        const errorMessage = getMessageFromErrorResponse(error);
        if (errorMessage) {
          message = errorMessage;
        }
      }

      notify({ message });
    },
  });

  return {
    createProject: createProjectMutation.mutateAsync,
    isError: createProjectMutation.isError,
    isCreating: createProjectMutation.isPending,
  };
};
